import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type LiveMeshSignal = {
  id: string;
  source: string;
  category: string;
  title: string;
  url: string;
  ts: string;
  snippet: string;
  severity: "low" | "medium" | "high" | "critical";
  priority: number;
  corroboration: number;
  drivers: string[];
};

export type LiveMeshCoverage = {
  catalogueSize: number;
  sampled: number;
  responded: number;
  empty: number;
  failed: number;
  wallMs: number;
  perCategory: Array<{ category: string; sampled: number; responded: number; signals: number }>;
  catalogueByCategory: Array<{ category: string; count: number }>;
};

export type LiveMeshResult = {
  signals: LiveMeshSignal[];
  coverage: LiveMeshCoverage;
  generatedAt: string;
};

export const fetchLiveMesh = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        size: z.number().int().min(20).max(240).default(140),
        categories: z.array(z.string().max(40)).max(20).optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data, context }): Promise<LiveMeshResult> => {
    const { runLiveMesh } = await import("./mesh.server");
    const { scoreSignals } = await import("./neural");
    const { catalogueStats } = await import("./catalogue");

    // Watchlist terms sharpen relevance for this specific analyst.
    let watchTerms: string[] = [];
    try {
      const { data: monitors } = await context.supabase
        .from("monitors")
        .select("target,label,enabled")
        .eq("user_id", context.userId)
        .eq("enabled", true)
        .limit(50);
      watchTerms = ((monitors ?? []) as any[])
        .flatMap((m) => [m.target, m.label])
        .filter((v): v is string => typeof v === "string" && v.trim().length >= 3);
    } catch {
      watchTerms = [];
    }

    const run = await runLiveMesh({ size: data.size, categories: data.categories });
    const scored = scoreSignals(run.signals, { watchTerms });

    const perCategory = new Map<string, { category: string; sampled: number; responded: number; signals: number }>();
    for (const o of run.outcomes) {
      const row = perCategory.get(o.category) ?? { category: o.category, sampled: 0, responded: 0, signals: 0 };
      row.sampled += 1;
      if (o.ok) row.responded += 1;
      row.signals += o.items;
      perCategory.set(o.category, row);
    }

    const signals = scored
      .sort((a, b) => b.priority - a.priority || (b.ts > a.ts ? 1 : -1))
      .slice(0, 150)
      .map((s) => ({
        id: s.id,
        source: s.source,
        category: s.category,
        title: s.title,
        url: s.url,
        ts: s.ts,
        snippet: s.snippet,
        severity: s.severity,
        priority: s.priority,
        corroboration: s.corroboration,
        drivers: s.drivers,
      }));

    return {
      signals,
      coverage: {
        catalogueSize: run.catalogueSize,
        sampled: run.sampled,
        responded: run.outcomes.filter((o) => o.ok).length,
        empty: run.outcomes.filter((o) => !o.ok && !o.error && o.httpStatus !== null).length,
        failed: run.outcomes.filter((o) => Boolean(o.error) || o.httpStatus === null).length,
        wallMs: run.wallMs,
        perCategory: Array.from(perCategory.values()).sort((a, b) => b.signals - a.signals),
        catalogueByCategory: catalogueStats().categories,
      },
      generatedAt: new Date().toISOString(),
    };
  });

export type LiveBrief = {
  brief: string;
  citations: string[];
  analyzed: number;
  generatedAt: string;
};

/** Grok synthesis layer over the scored mesh output. Never invents signals. */
export const analyzeLiveMesh = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        signals: z
          .array(
            z.object({
              source: z.string().max(200),
              title: z.string().max(400),
              url: z.string().max(600),
              ts: z.string().max(40),
              snippet: z.string().max(400).default(""),
              severity: z.string().max(20),
              priority: z.number(),
              corroboration: z.number(),
            }),
          )
          .max(60),
        environment: z.enum(["sandbox", "live"]).default("live"),
      })
      .parse(d),
  )
  .handler(async ({ data, context }): Promise<LiveBrief> => {
    const { requireEntitlement } = await import("@/lib/payments/entitlements.server");
    await requireEntitlement({
      supabase: context.supabase,
      userId: context.userId,
      feature: "ai",
      environment: data.environment,
    });

    if (!data.signals.length) {
      return {
        brief:
          "No live signals were available in this polling window, so there is nothing to assess. Re-run the sweep or widen the category filter — no assessment is produced without collected evidence.",
        citations: [],
        analyzed: 0,
        generatedAt: new Date().toISOString(),
      };
    }

    const corpus = data.signals
      .slice(0, 60)
      .map(
        (s, i) =>
          `[${i + 1}] (${s.severity}, priority ${s.priority}, ${s.corroboration} source${s.corroboration > 1 ? "s" : ""}) ${s.title}\n    source: ${s.source} · ${s.ts}\n    url: ${s.url}\n    context: ${s.snippet}`,
      )
      .join("\n");

    let watchLine = "";
    try {
      const { data: monitors } = await context.supabase
        .from("monitors")
        .select("target,target_type,enabled")
        .eq("user_id", context.userId)
        .eq("enabled", true)
        .limit(30);
      const list = ((monitors ?? []) as any[]).map((m) => `${m.target} (${m.target_type})`);
      if (list.length) watchLine = `Analyst watchlist: ${list.join(", ")}.\n`;
    } catch {
      /* watchlist is optional context */
    }

    const { callGrok } = await import("@/lib/intel/grok.server");
    const res = await callGrok(
      [
        {
          role: "system",
          content:
            "You are GROK-AGENT, the live-monitoring triage analyst for CARIO INTEL. You reason ONLY over the numbered signals supplied by the collection mesh. Rules: never invent an event, source, number, date or entity that is not in the input; cite signals by their [n] index; if the evidence is thin, say so explicitly rather than padding; do not speculate about non-public information; legal public OSINT only. Output concise Markdown with sections: Situation, Priority items (bulleted, each citing [n]), Watchlist impact, Confidence and gaps.",
        },
        {
          role: "user",
          content: `${watchLine}Collected live signals (${data.signals.length}) from the monitoring mesh:\n\n${corpus}\n\nProduce the triage brief now.`,
        },
      ],
      { liveSearch: false },
    );

    return {
      brief: res.content?.trim() || "The analysis layer returned no content for this batch; no assessment is asserted.",
      citations: (res.citations ?? []).slice(0, 12),
      analyzed: data.signals.length,
      generatedAt: new Date().toISOString(),
    };
  });
